import React, { useEffect, useMemo, useState } from "react";
import { motion } from "motion/react";
import { Cell, Pie, PieChart, Tooltip } from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "../../../app/components/ui/card";
import { api } from "../../../lib/api";
import { formatInrLoose } from "../../../lib/currency";
import { useAuth } from "../../../providers/AuthProvider";

type PieRow = { business: string; sales: number };

const COLORS = [
  "var(--chart-2)",
  "var(--chart-1)",
  "var(--chart-3)",
  "var(--chart-4)",
  "var(--destructive)",
  "var(--chart-2)",
  "var(--chart-5)",
];

export function SalesByBusinessPieChart({ performanceRows }: { performanceRows?: PieRow[] }) {
  const { token } = useAuth();

  const [rows, setRows] = useState<PieRow[]>([]);

  const total = useMemo(() => rows.reduce((s, r) => s + r.sales, 0), [rows]);

  useEffect(() => {
    if (performanceRows !== undefined) {
      const data = [...performanceRows]
        .filter((r) => r.business !== "" && r.sales >= 0)
        .sort((a, b) => b.sales - a.sales);
      setRows(data.slice(0, 5));
    }
  }, [performanceRows]);

  useEffect(() => {
    if (performanceRows !== undefined) return;
    if (!token) return;

    let cancelled = false;
    (async () => {
      const now = new Date();
      const to = new Date(now);
      const from = new Date(now);
      from.setDate(now.getDate() - 29);

      const toYmd = (d: Date) => {
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, "0");
        const dd = String(d.getDate()).padStart(2, "0");
        return `${yyyy}-${mm}-${dd}`;
      };

      try {
        const res = await api.adminReports.getBusinessPerformance({
          token,
          filters: { from_date: toYmd(from), to_date: toYmd(to) },
        });

        if (cancelled) return;

        const data = ((res.data as any[]) ?? [])
          .map((r) => ({ business: String(r.business_name ?? ""), sales: Number(r.total_sales ?? 0) }))
          .filter((r) => r.business !== "" && r.sales >= 0)
          .sort((a, b) => b.sales - a.sales);

        setRows(data.slice(0, 5));
      } catch {
        if (cancelled) return;
        setRows([]);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [token, performanceRows]);

  return (
    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }}>
      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle>Total sales by business</CardTitle>
          <div className="text-sm text-muted-foreground">
            Total: <span className="text-foreground font-medium">{formatInrLoose(total)}</span>
          </div>
        </CardHeader>
        <CardContent>
          <div className="h-[320px]">
            <PieChart>
              <Tooltip
                formatter={(value: number, name: string) => [formatInrLoose(value), name === "business" ? "Sales" : name]}
              />
              <Pie
                data={rows}
                dataKey="sales"
                nameKey="business"
                innerRadius={70}
                outerRadius={110}
                strokeWidth={2}
              >
                {rows.map((_, idx) => (
                  <Cell key={idx} fill={COLORS[idx % COLORS.length]} />
                ))}
              </Pie>
            </PieChart>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}
